// src/pages/CreateAutomation.tsx

import React, { useEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiEdit2, FiSave } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { RootState } from '../../app/store';
import {
  saveAutomation,
  updateAutomationData,
} from '../../features/automation/automationSlice';
import HierarchyGraph from './HierarchyGraph';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import { createAutomationDraft } from './automationApi';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEdit2Component = FiEdit2 as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiSaveComponent = FiSave as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

export default function CreateAutomation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { automationFlowName, trigger, conditions } = useSelector(
    (state: RootState) => state.automation
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(
    automationFlowName || 'untitled'
  );
  const hierarchyGraphRef = useRef<{ nodes: any[]; edges: any[] } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [automationId, setAutomationId] = useState<string | null>(null);

  // Updates the edited name state when the automation flow name changes
  useEffect(() => {
    setEditedName(automationFlowName || 'untitled');
  }, [automationFlowName]);

  // Saves the edited automation flow name to the Redux store and displays a success toast
  // Validates that the name is not empty and has changed
  const handleSave = () => {
    if (editedName && editedName.trim() !== automationFlowName) {
      if (editedName.trim() === '') {
        toast.warn('Flow name cannot be empty!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        return;
      }
      dispatch(updateAutomationData({ automationFlowName: editedName }));
      toast.success('Automation flow name updated successfully!');
    }
    setIsEditing(false);
  };

  // Cancels editing the automation flow name and reverts to the original name
  const handleCancel = () => {
    setEditedName(automationFlowName || 'untitled');
    setIsEditing(false);
  };

  // Checks if the edited name differs from the current automation flow name
  const isNameChanged = editedName.trim() !== automationFlowName;

  // Saves the automation as a draft to the API and Redux store
  const handleSaveAsDraft = async () => {
    if (isSaving) return;

    // Check if the name is still 'untitled' (default)
    if (automationFlowName === 'untitled' || !automationFlowName?.trim()) {
      toast.warn('Please change the automation name from "untitled" before saving as draft!', {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    if (hierarchyGraphRef.current) {
      const { nodes, edges } = hierarchyGraphRef.current;

      // Validate required fields
      const hasTagNode = nodes.some(
        (node) => node.data.type === 'tags' && !node.data.tag
      );
      if (hasTagNode) {
        toast.error('Please fill all required tag fields before saving!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        return;
      }

      // Get auth context
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        return;
      }

      setIsSaving(true);

      try {
        // Call the API to create automation draft with conditions
        const result = await createAutomationDraft(
          automationFlowName,
          trigger || '',
          nodes,
          edges,
          token,
          tenantId,
          userId,
          BASE_URL,
          conditions || []
        );

        // Store the automation ID for publishing
        setAutomationId(result.id);

        // Save to Redux store
        const simplifiedNodes = nodes.map((node) => ({
          id: node.id,
          label: node.data.label,
          type: node.data.type,
          position: node.position,
          tag: node.data.tag || '',
          selectedTags: node.data.selectedTags || [],
          template: node.data.template || '',
          variantId: node.data.variantId || '',
          variables: node.data.variables || { name: '', address: '' },
          delayType: node.data.delayType || '',
          amount: node.data.amount || 0,
          unit: node.data.unit || '',
          date: node.data.date || '',
          time: node.data.time || '',
          conditions: node.data.conditions || [],
          maxWaitAmount: node.data.maxWaitAmount || 0,
          maxWaitUnit: node.data.maxWaitUnit || '',
          buckets: node.data.buckets || [],
        }));

        const simplifiedEdges = edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
        }));

        dispatch(
          saveAutomation({
            id: result.id,
            name: automationFlowName,
            trigger: trigger || '',
            status: 'draft',
            nodes: simplifiedNodes,
            edges: simplifiedEdges,
          })
        );

        toast.success('Automation saved as draft successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });

        navigate('/automations');

        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (error: any) {
        // console.error('Error saving automation:', error);
        toast.error(error.message || 'Failed to save automation draft. Please try again.', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'colored',
          style: { backgroundColor: '#fef2f2', color: '#dc2626' },
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Publishes the automation to the API and Redux store
  const handlePublish = async () => {
    if (isSaving) return;

    // Check if the name is still 'untitled' (default)
    if (automationFlowName === 'untitled' || !automationFlowName?.trim()) {
      toast.warn('Please change the automation name from "untitled" before publishing!', {
        position: 'top-right',
        autoClose: 4000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    if (!trigger) {
      toast.warn('Please select a trigger before publishing!', {
        position: 'top-right',
        autoClose: 3000,
        theme: 'colored',
        style: { backgroundColor: '#fef3c7', color: '#d97706' },
      });
      return;
    }

    if (hierarchyGraphRef.current) {
      const { nodes, edges } = hierarchyGraphRef.current;

      // Check for empty tag fields
      const hasTagNode = nodes.some(
        (node) => node.data.type === 'tags' && !node.data.tag
      );
      if (hasTagNode) {
        toast.error('Please fill all required tag fields before publishing!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
          style: { backgroundColor: '#fef3c7', color: '#d97706' },
        });
        return;
      }

      // Check for Wait Until nodes with empty maxWait fields
      const hasEmptyWaitUntilNode = nodes.some(
        (node) =>
          node.data.type === 'wait' &&
          (!node.data.maxWaitAmount || !node.data.maxWaitUnit)
      );
      if (hasEmptyWaitUntilNode) {
        toast.error(
          'Please fill all max wait amount and unit fields for Wait Until nodes before publishing!',
          {
            position: 'top-right',
            autoClose: 3000,
            theme: 'colored',
            style: { backgroundColor: '#fef3c7', color: '#d97706' },
          }
        );
        return;
      }

      // Get auth context
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required. Please log in again.', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });
        return;
      }

      setIsSaving(true);

      try {
        let finalAutomationId = automationId;

        // If we don't have an automation ID yet, create a draft first
        if (!finalAutomationId) {
          const draftResult = await createAutomationDraft(
            automationFlowName,
            trigger,
            nodes,
            edges,
            token,
            tenantId,
            userId,
            BASE_URL,
            conditions || []
          );
          finalAutomationId = draftResult.id;
          setAutomationId(finalAutomationId);
        }

        // Call the API to publish automation (no body required)
        // const result = await publishAutomation(
        //   finalAutomationId,
        //   token,
        //   tenantId,
        //   userId,
        //   BASE_URL
        // );

        // Save to Redux store
        const simplifiedNodes = nodes.map((node) => ({
          id: node.id,
          label: node.data.label,
          type: node.data.type,
          position: node.position,
          tag: node.data.tag || '',
          selectedTags: node.data.selectedTags || [],
          template: node.data.template || '',
          variantId: node.data.variantId || '',
          variables: node.data.variables || { name: '', address: '' },
          delayType: node.data.delayType || '',
          amount: node.data.amount || 0,
          unit: node.data.unit || '',
          date: node.data.date || '',
          time: node.data.time || '',
          conditions: node.data.conditions || [],
          maxWaitAmount: node.data.maxWaitAmount || 0,
          maxWaitUnit: node.data.maxWaitUnit || '',
          buckets: node.data.buckets || [],
        }));

        const simplifiedEdges = edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
        }));

        dispatch(
          saveAutomation({
            id: finalAutomationId,
            name: automationFlowName,
            trigger,
            status: 'published',
            nodes: simplifiedNodes,
            edges: simplifiedEdges,
          })
        );

        toast.success('Automation published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          theme: 'colored',
        });

        navigate('/automations');

        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (error: any) {
        // console.error('Error publishing automation:', error);
        toast.error(error.message || 'Failed to publish automation. Please try again.', {
          position: 'top-right',
          autoClose: 4000,
          theme: 'colored',
          style: { backgroundColor: '#fef2f2', color: '#dc2626' },
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className='p-2'>
      <button
        onClick={() => navigate(-1)}
        className='flex items-center text-[14px] text-[#00899E] mb-4 hover:underline font-semibold'
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Automations
      </button>

      <div className='flex justify-between items-center flex-wrap gap-[10px]'>
        <div>
          <h2 className='text-[20px] font-bold text-[#3F5265]'>
            Setup Automation
          </h2>
          <p className='text-[#657584] mb-6 text-[16px]'>
            Set up your campaign to reach your audience
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <button
            onClick={handleSaveAsDraft}
            disabled={isSaving}
            className={`px-4 py-2 rounded-md border border-[#FF6B4A] text-[#FF6B4A] hover:bg-[#fff2ef] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {isSaving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            onClick={handlePublish}
            disabled={isSaving}
            className={`px-4 py-2 rounded-md bg-[#FF6B4A] text-white hover:bg-[#ff5833] ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
          >
            {isSaving ? 'Publishing...' : 'Publish Automation'}
          </button>
        </div>
      </div>
      <div className='bg-[#e7eef3] rounded-md border border-gray-200 shadow-sm'>
        <div
          className='flex items-center justify-start border-b border-gray-200 bg-white rounded-t-md mb-4 p-4'
          style={{ boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }}
        >
          <div className='flex items-center'>
            {isEditing ? (
              <input
                type='text'
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className='text-[16px] font-semibold text-[#3F5265] mr-2 border border-gray-300 rounded-md p-1 focus:ring-2 focus:ring-teal-400'
                autoFocus
              />
            ) : (
              <span className='text-[16px] font-semibold text-[#3F5265] mr-2'>
                {automationFlowName || 'untitled'}
              </span>
            )}
            <span className='px-2 py-1 rounded-full text-xs font-medium bg-[#FEF3F2] text-[#F04438]'>
              Draft
            </span>
          </div>
          <div className='flex items-center gap-2 ml-3'>
            {!isEditing && (
              <button
                onClick={() => {
                  setIsEditing(true);
                  setEditedName(automationFlowName || 'untitled');
                }}
                className='relative rounded-sm border border-teal-600 p-1 px-2 shadow-md cursor-pointer hover:bg-teal-50 group'
              >
                <FiEdit2Component size={16} className='text-teal-600' />
                <span className='absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                  Edit
                </span>
              </button>
            )}
            {isEditing && (
              <>
                <button
                  onClick={handleSave}
                  disabled={!isNameChanged}
                  className={`relative rounded-sm p-1 px-2 shadow-md cursor-pointer group ${!isNameChanged
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                >
                  <FiSaveComponent size={18} />
                  <span className='absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                    Save
                  </span>
                </button>
                <button
                  onClick={handleCancel}
                  className='relative group rounded-sm border border-teal-600 p-1 px-2 shadow-md cursor-pointer hover:bg-teal-50'
                >
                  <FiEdit2Component size={16} className='text-teal-600' />
                  <span className='absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap'>
                    Close Edit
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        <HierarchyGraph ref={hierarchyGraphRef} />
      </div>
      <ToastContainer />
    </div>
  );
}